<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import type { Article, Resolved } from '@/types'
import { getAuthor, PATHS } from '@/content'
import { useBlogStore, primaryCategoryIn } from '@/lib/blog'
import { baseHead, articleLd, breadcrumbLd } from '@/lib/seo'
import { formatDate } from '@/lib/format'
import BreadcrumbBar from '@/components/BreadcrumbBar.vue'
import ArticleCard from '@/components/ArticleCard.vue'
import CtaProjet from '@/components/CtaProjet.vue'

const props = defineProps<{ resolved: Resolved }>()
const article = computed(() => props.resolved.article as Article)

// Article servi en direct : corps, categories et lectures liees viennent du blog live.
const blog = useBlogStore()
const cat = computed(() => primaryCategoryIn(article.value, blog.payload?.categories || []))
const authorName = computed(
  () => article.value.author_name || getAuthor(article.value.author_id)?.name || undefined,
)
const related = computed(() => blog.payload?.related || [])
const contentHtml = computed(() => article.value.content_html || '')

const crumbs = computed(() => {
  const c = [
    { label: 'Accueil', path: '/' },
    { label: 'Actualités', path: PATHS.blog },
  ]
  if (cat.value) c.push({ label: cat.value.name, path: cat.value.path })
  c.push({ label: article.value.title, path: article.value.path })
  return c
})

useHead(() => {
  const a = article.value
  return {
    ...baseHead({
      title: a.seo?.seo_title || a.title,
      description: a.seo?.seo_description || a.excerpt,
      canonical: a.seo?.canonical || a.path,
      index: a.seo?.robots_index !== false,
      image: a.seo?.og_image || a.featured_image || '',
      type: 'article',
      publishedTime: a.date,
      modifiedTime: a.modified,
    }),
    script: [articleLd(a, authorName.value), breadcrumbLd(crumbs.value)],
  }
})
</script>

<template>
  <article class="art">
    <section class="art-hero">
      <div class="container art-hero__in">
        <div class="art-hero__meta">
          <router-link v-if="cat" :to="cat.path" class="art-hero__cat">{{ cat.name }}</router-link>
          <time :datetime="article.date">{{ formatDate(article.date) }}</time>
        </div>
        <h1>{{ article.title }}</h1>
        <p v-if="article.excerpt" class="art-hero__excerpt">{{ article.excerpt }}</p>
      </div>
    </section>

    <BreadcrumbBar :crumbs="crumbs" />

    <div
      v-if="article.featured_image"
      class="art-figure container"
    >
      <img
        :src="article.featured_image"
        :alt="article.title"
        fetchpriority="high"
        width="1000"
        height="560"
      />
    </div>

    <section class="section content">
      <div class="container">
        <div class="prose" v-html="contentHtml"></div>
      </div>
    </section>

    <CtaProjet />

    <section v-if="related.length" class="section section--alt">
      <div class="container">
        <span class="eyebrow">À lire aussi</span>
        <h2>Articles liés</h2>
        <div class="grid grid--3 art-related">
          <ArticleCard v-for="r in related" :key="r.path" :article="r" />
        </div>
      </div>
    </section>
  </article>
</template>

<style scoped>
.art-hero {
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border);
  padding: 44px 0;
}
.art-hero__in {
  max-width: 860px;
}
.art-hero__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.85rem;
  color: var(--muted);
  margin-bottom: 10px;
}
.art-hero__cat {
  background: var(--green-soft);
  color: var(--green-dark);
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 999px;
}
.art-hero__cat:hover {
  text-decoration: none;
  background: var(--green);
  color: #272a23;
}
.art-hero__excerpt {
  font-size: 1.15rem;
  color: var(--title);
  margin: 12px 0 0;
}
.art-figure {
  margin-top: 28px;
}
.art-figure img {
  width: 100%;
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  max-height: 520px;
  object-fit: cover;
}
.content .prose {
  margin: 0 auto;
}
.art-related {
  margin-top: 22px;
}
</style>
